<script setup lang="tsx">
import { getResponsibleIcon } from '@/services/utils'
import { useI18n } from 'vue-i18n'
import { formatPrice, formatRiskScoreStr } from '../shared'
import type { ImpactedAsset } from '@/models/api-generated'
import { computed } from 'vue'
import RiskScoreBadge from '../RiskScoreBadge.vue'

const model = defineModel<ImpactedAsset[]>({ required: true })

const summarizeAssets = (assets: ImpactedAsset[]) => {
	const summary = assets.reduce(
		(acc, current) => {
			if (current.responsible) acc.responsibleCount += 1
			acc.price += current.price
			acc.riskScore += current.riskScore
			return acc
		},
		{
			assetsCount: model.value.length,
			responsibleCount: 0,
			riskScore: 0,
			price: 0,
		},
	)

	if (assets.length) summary.riskScore = summary.riskScore / assets.length
	return summary
}

const summary = computed(() => summarizeAssets(model.value))

const { t } = useI18n()

function goToAssets(asset: string) {
	const params = new URLSearchParams({ name: asset })
	window.location.href = `/app/assets?${params}`
}
</script>

<template>
	<div class="card custom-card">
		<div class="card-body">
			<h6 class="card-title text-center fw-bold">{{ t('risk.impactedAsset', 2) }}</h6>
			<div class="table-responsive w-100">
				<table class="impacted-assets-table">
					<thead>
						<tr>
							<th>{{ t('risk.asset', 2) }}</th>
							<th>{{ t('risk.responsible', 2) }}</th>
							<th>{{ t('risk.residualRiskScore') }}</th>
							<th>{{ t('risk.price') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr class="summary-row">
							<td>{{ summary.assetsCount ?? '-' }}</td>
							<td>{{ summary.responsibleCount ?? '-' }}</td>
							<td>{{ formatRiskScoreStr(summary.riskScore) ?? '-' }}</td>
							<td>{{ formatPrice(summary.price) }}</td>
						</tr>
					</tbody>
					<tbody>
						<tr v-for="a in model" class="asset-row" :key="a.id">
							<td>
								<div class="d-inline-flex align-items-center">
									<span
										@click.stop="goToAssets(a.name)"
										style="cursor: pointer"
										class="badge bg-primary flex-shrink-0 me-2"
										v-one-tooltip="$t('risk.filterAsset')"
										>A</span
									>
									{{ a.name }}
								</div>
							</td>
							<td>
								<i v-if="a.responsible" :class="[getResponsibleIcon(a.responsible)]"></i>{{ a.responsible ? a.responsible.label : '-' }}
							</td>
							<td class="align-middle">
								<div class="d-flex align-items-center">
									<RiskScoreBadge v-model="a.riskScore" />
								</div>
							</td>
							<td>{{ formatPrice(a.price) }}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<style lang="sass" scoped>
.custom-card
  background-color: var(--bs-body-bg)
  color: var(--bs-body-color)
  border-color: var(--bs-border-color)

.impacted-assets-table
  width: 100%
  border-collapse: separate
  border-spacing: 0 4px
  color: var(--bs-body-color)

  th,
  td
    padding: .5rem 1rem

  thead
    font-weight: bold
    color: var(--bs-body-color)

    th
      background-color: transparent

.summary-row
  td
    border-bottom: 1px solid var(--bs-border-color)

.asset-row
  td
    background-color: var(--bs-secondary-bg)
    vertical-align: top
    color: var(--bs-body-color)
    border-bottom: 2px solid var(--bs-primary-bg-subtle)

  td:first-child
    border-top-left-radius: 8px
    border-bottom-left-radius: 8px

  td:last-child
    border-top-right-radius: 8px
    border-bottom-right-radius: 8px
</style>
